import { useEffect, useState, useContext } from 'react';
import { Outlet } from 'react-router-dom';

import useMediaQuery from '@mui/material/useMediaQuery';
import Toolbar from '@mui/material/Toolbar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';

// project imports
import Drawer from './Drawer';
import Header from './Header';
import Footer from './Footer';
import Loader from 'components/Loader';
import Breadcrumbs from 'components/@extended/Breadcrumbs';
import ScrollTop from 'components/ScrollTop';
import VerificationBanner from 'components/verification/VerificationBanner';
import AccountVerificationModal from 'sections/verification/AccountVerificationModal';
import AuthContext from 'contexts/AuthContext';
import { useMode } from 'contexts/ModeContext';

import { handlerDrawerOpen, useGetMenuMaster } from 'api/menu';

// ==============================|| MAIN LAYOUT ||============================== //

export default function DashboardLayout() {
  const { menuMasterLoading } = useGetMenuMaster();
  const { user } = useContext(AuthContext);
  const { mode, isSandbox, canUseLive } = useMode();
  const downXL = useMediaQuery((theme) => theme.breakpoints.down('xl'));

  const [openVerificationModal, setOpenVerificationModal] = useState(false);
  const [hasAutoOpened, setHasAutoOpened] = useState(false);

  // set media wise responsive drawer
  useEffect(() => {
    handlerDrawerOpen(!downXL);
  }, [downXL]);

  // Verificar automaticamente se o utilizador necessita de validação
  useEffect(() => {
    if (user && !hasAutoOpened) {
      const status = user.verification_status || 'unverified';
      if (status === 'unverified' || status === 'rejected') {
        setOpenVerificationModal(true);
        setHasAutoOpened(true);
      }
    }
  }, [user, hasAutoOpened]);

  if (menuMasterLoading) return <Loader />;

  const hasAdminToken = !!localStorage.getItem('gpo_admin_token');

  return (
    <Box sx={{ display: 'flex', width: '100%' }}>
      <ScrollTop />
      <Header />
      <Drawer />

      <Box component="main" sx={{ width: 'calc(100% - 260px)', flexGrow: 1, p: { xs: 2, sm: 3 } }}>
        <Toolbar sx={{ mt: 'inherit' }} />
        
        {hasAdminToken && (
          <Alert 
            severity="warning" 
            sx={{ mb: 3 }}
            action={
              <Button 
                color="inherit" 
                size="small" 
                onClick={() => {
                  localStorage.removeItem('gpo_token');
                  window.location.href = '/admin/merchants';
                }}
              >
                Voltar
              </Button>
            }
          >
            Está a ver a plataforma em nome de uma empresa.
          </Alert>
        )}

        {/* Banner de Validação da Conta */}
        <VerificationBanner 
          user={user} 
          onOpenModal={() => setOpenVerificationModal(true)} 
        />

        {/* Aviso de modo Sandbox (contas validadas que escolheram ver dados de teste) */}
        {isSandbox && canUseLive && (
          <Alert severity="warning" sx={{ mb: 2.5 }}>
            <strong>Modo Sandbox:</strong> está a ver dados de teste. Os pagamentos aqui não são reais. Mude para Produção no topo da página.
          </Alert>
        )}

        <Box
          sx={{
            ...{ px: { xs: 0, sm: 2 } },
            position: 'relative',
            minHeight: 'calc(100vh - 110px)',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <Breadcrumbs />
          {/* key={mode}: ao trocar Produção/Sandbox, a página remonta e recarrega os dados */}
          <Outlet key={mode} />
          <Footer />
        </Box>
      </Box>

      {/* Modal de Validação de Conta (Nome Individual vs Empresa) */}
      <AccountVerificationModal
        open={openVerificationModal}
        onClose={() => setOpenVerificationModal(false)}
      />
    </Box>
  );
}
